import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import type {
  CartItem,
  CartSummary,
  ServerCartItem,
  ServerCartResponse,
  AddToCartPayload,
  CartRevalidationResult,
  CartRevalidationChange,
} from '../types/cart.types'
import {
  addToServerCart,
  updateServerCartItem,
  removeServerCartItem,
  clearServerCart,
  revalidateCart,
  mergeGuestCart,
} from '../services/cartService'
import {
  loadGuestCart,
  saveGuestCart,
  clearGuestCartStorage,
  guestItemKey,
} from '../utils/guestPersistence'
import { computeCartSummary, generateId, clamp } from '../utils/cartMath'

// ─── State ────────────────────────────────────────────────────────────────────

export interface CartState {
  /** Source of truth */
  items: CartItem[]
  summary: CartSummary
  /** true = operating on server cart (authenticated user) */
  isAuthenticated: boolean
  /** true while any cart API call is in flight */
  syncing: boolean
  /** true after initial load completes */
  hydrated: boolean
  /** Tracks revalidation changes for display */
  revalidationChanges: CartRevalidationChange[]
  /** Set when server revalidation finds issues */
  hasInvalidItems: boolean
  /** Error message for toast/banner display */
  lastError: string | null
}

// ─── Actions ──────────────────────────────────────────────────────────────────

export interface CartActions {
  /** Call once on app init — loads guest cart or fetches server cart */
  hydrate: (isAuthenticated: boolean) => Promise<void>
  /** Call on login — triggers guest→auth merge then clears guest storage */
  onLogin: () => Promise<void>
  /** Call on logout — clears auth state, loads (or retains) guest cart */
  onLogout: () => void
  addItem: (payload: AddToCartPayload & {
    // UI metadata — not sent to server directly
    slug: string
    name: string
    brand: string
    image: string
    pricing: CartItem['pricing']
    max_quantity: number
  }) => Promise<void>
  updateQuantity: (itemId: string, quantity: number) => Promise<void>
  removeItem: (itemId: string) => Promise<void>
  clearCart: () => Promise<void>
  /** Re-fetch server pricing — call periodically or before checkout */
  revalidate: () => Promise<void>
  setFromServer: (response: ServerCartResponse) => void
  clearError: () => void
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function serverItemToCartItem(item: ServerCartItem): CartItem {
  return {
    ...item,
    unit_price: item.pricing.effective_price,
    subtotal: Math.round(item.quantity * item.pricing.effective_price),
  }
}

const EMPTY_SUMMARY: CartSummary = {
  subtotal: 0, total_savings: 0, shipping_estimate: 0,
  tax: 0, total: 0, currency: 'KES', item_count: 0, unit_count: 0,
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useCartStore = create<CartState & CartActions>()(
  subscribeWithSelector((set, get) => ({
    items: [],
    summary: EMPTY_SUMMARY,
    isAuthenticated: false,
    syncing: false,
    hydrated: false,
    revalidationChanges: [],
    hasInvalidItems: false,
    lastError: null,

    // ── Hydration ───────────────────────────────────────────────────────────

    hydrate: async (isAuthenticated) => {
      // Guard: never re-hydrate once settled — prevents races with in-flight
      // addItem optimistic updates when AuthBootstrap re-evaluates its effect.
      if (get().hydrated) return

      set({ isAuthenticated })

      if (isAuthenticated) {
        // Authenticated: fetch server cart
        const { fetchServerCart } = await import('../services/cartService')
        try {
          const serverCart = await fetchServerCart()
          get().setFromServer(serverCart)
        } catch {
          // Server unreachable — fall back to empty cart; don't show error on init
        }
      } else {
        // Guest: load from localStorage
        const items = loadGuestCart()
        const summary = computeCartSummary(items)
        set({ items, summary })
      }

      set({ hydrated: true })
    },

    // ── Login: merge guest → auth ────────────────────────────────────────────

    onLogin: async () => {
      const { items: guestItems } = get()
      set({ isAuthenticated: true, syncing: true })

      try {
        const serverCart = await mergeGuestCart({
          strategy: 'sum_quantities',
          guest_items: guestItems.map((i) => ({
            product_id: i.product_id,
            variant_id: i.variant_id,
            quantity: i.quantity,
          })),
        })
        get().setFromServer(serverCart)
        clearGuestCartStorage()
      } catch {
        // Merge failed — just fetch server cart (guest items lost, acceptable)
        try {
          const { fetchServerCart } = await import('../services/cartService')
          const serverCart = await fetchServerCart()
          get().setFromServer(serverCart)
        } catch {
          set({ lastError: 'Could not sync your cart. Please refresh.' })
        }
      } finally {
        set({ syncing: false })
      }
    },

    // ── Logout: fall back to guest (empty) cart ──────────────────────────────

    onLogout: () => {
      clearGuestCartStorage()
      set({
        items: [],
        summary: EMPTY_SUMMARY,
        isAuthenticated: false,
        revalidationChanges: [],
        hasInvalidItems: false,
        lastError: null,
        // Reset hydrated so the next hydrate() call (guest) is allowed through
        hydrated: false,
      })
    },

    // ── Add item ─────────────────────────────────────────────────────────────

    addItem: async (payload) => {
      const { isAuthenticated, items } = get()
      console.log('[cartStore.addItem] isAuthenticated:', isAuthenticated)
      const key = guestItemKey(payload.product_id, payload.variant_id)

      // ── Guest: local operation ─────────────────────────────────────────────
      if (!isAuthenticated) {
        const existingIdx = items.findIndex(
          (i) => guestItemKey(i.product_id, i.variant_id) === key,
        )

        if (existingIdx >= 0) {
          const existing = items[existingIdx]
          const newQty = clamp(existing.quantity + payload.quantity, 1, payload.max_quantity)
          const updated = items.map((item, i) =>
            i === existingIdx
              ? { ...item, quantity: newQty, subtotal: Math.round(newQty * item.unit_price) }
              : item,
          )
          const summary = computeCartSummary(updated)
          set({ items: updated, summary })
          saveGuestCart(updated)
          return
        }

        const newItem: CartItem = {
          id: generateId(),
          product_id: payload.product_id,
          variant_id: payload.variant_id,
          slug: payload.slug,
          name: payload.name,
          brand: payload.brand,
          image: payload.image,
          quantity: clamp(payload.quantity, 1, payload.max_quantity),
          max_quantity: payload.max_quantity,
          pricing: payload.pricing,
          unit_price: payload.pricing.effective_price,
          subtotal: Math.round(payload.quantity * payload.pricing.effective_price),
          status: 'valid',
          added_at: new Date().toISOString(),
        }
        const updated = [...items, newItem]
        const summary = computeCartSummary(updated)
        set({ items: updated, summary })
        saveGuestCart(updated)
        return
      }

      // ── Auth: optimistic update → server sync ──────────────────────────────
      set({ syncing: true })

      const existingIdx = items.findIndex(
        (i) => guestItemKey(i.product_id, i.variant_id) === key,
      )
      const optimisticItems = existingIdx >= 0
        ? items.map((item, i) => {
            if (i !== existingIdx) return item
            const newQty = clamp(item.quantity + payload.quantity, 1, payload.max_quantity)
            return { ...item, quantity: newQty, subtotal: Math.round(newQty * item.unit_price) }
          })
        : [
            ...items,
            {
              id: `optimistic_${generateId()}`,
              product_id: payload.product_id,
              variant_id: payload.variant_id,
              slug: payload.slug,
              name: payload.name,
              brand: payload.brand,
              image: payload.image,
              quantity: payload.quantity,
              max_quantity: payload.max_quantity,
              pricing: payload.pricing,
              unit_price: payload.pricing.effective_price,
              subtotal: Math.round(payload.quantity * payload.pricing.effective_price),
              status: 'valid' as const,
              added_at: new Date().toISOString(),
            },
          ]

      set({ items: optimisticItems, summary: computeCartSummary(optimisticItems) })

      try {
        const serverCart = await addToServerCart({
          product_id: payload.product_id,
          variant_id: payload.variant_id || undefined,
          quantity: payload.quantity,
        })
        get().setFromServer(serverCart)
      } catch (err: unknown) {
        // Rollback to pre-optimistic state
        set({
          items,
          summary: computeCartSummary(items),
          lastError: 'Could not add item to cart. Please try again.',
        })
      } finally {
        set({ syncing: false })
      }
    },

    // ── Update quantity ───────────────────────────────────────────────────────

    updateQuantity: async (itemId, quantity) => {
      const { isAuthenticated, items } = get()
      const itemIdx = items.findIndex((i) => i.id === itemId)
      if (itemIdx < 0) return

      const item = items[itemIdx]
      const safeQty = clamp(quantity, 1, item.max_quantity)

      // Optimistic update (both guest and auth)
      const updated = items.map((i, idx) =>
        idx === itemIdx
          ? { ...i, quantity: safeQty, subtotal: Math.round(safeQty * i.unit_price) }
          : i,
      )
      set({ items: updated, summary: computeCartSummary(updated) })

      if (!isAuthenticated) {
        saveGuestCart(updated)
        return
      }

      set({ syncing: true })
      try {
        const serverCart = await updateServerCartItem(itemId, { item_id: itemId, quantity: safeQty })
        get().setFromServer(serverCart)
      } catch {
        // Rollback
        set({ items, summary: computeCartSummary(items), lastError: 'Could not update quantity.' })
      } finally {
        set({ syncing: false })
      }
    },

    // ── Remove item ───────────────────────────────────────────────────────────

    removeItem: async (itemId) => {
      const { isAuthenticated, items } = get()
      const filtered = items.filter((i) => i.id !== itemId)

      // Optimistic remove
      set({ items: filtered, summary: computeCartSummary(filtered) })

      if (!isAuthenticated) {
        saveGuestCart(filtered)
        return
      }

      set({ syncing: true })
      try {
        const serverCart = await removeServerCartItem(itemId)
        get().setFromServer(serverCart)
      } catch {
        // Rollback
        set({ items, summary: computeCartSummary(items), lastError: 'Could not remove item.' })
      } finally {
        set({ syncing: false })
      }
    },

    // ── Clear cart ────────────────────────────────────────────────────────────

    clearCart: async () => {
      const { isAuthenticated } = get()
      set({ items: [], summary: EMPTY_SUMMARY })

      if (!isAuthenticated) {
        clearGuestCartStorage()
        return
      }

      set({ syncing: true })
      try {
        await clearServerCart()
      } catch {
        set({ lastError: 'Could not clear cart.' })
      } finally {
        set({ syncing: false })
      }
    },

    // ── Revalidation ──────────────────────────────────────────────────────────

    revalidate: async () => {
      const { isAuthenticated } = get()
      if (!isAuthenticated) return // Guest carts revalidated at checkout only

      set({ syncing: true })
      try {
        const result: CartRevalidationResult = await revalidateCart()
        const items = result.items.map(serverItemToCartItem)
        const summary = result.summary
        set({
          items,
          summary,
          revalidationChanges: result.changes,
          hasInvalidItems: result.has_invalid_items,
        })
      } catch {
        set({ lastError: 'Could not refresh cart pricing. Prices may be out of date.' })
      } finally {
        set({ syncing: false })
      }
    },

    // ── Set from server (canonical) ───────────────────────────────────────────
    // NOTE: does NOT clear revalidationChanges — that is only done by revalidate()
    // so that banners survive the setFromServer call triggered by addItem/remove/etc.

    setFromServer: (response) => {
      const items = response.items.map(serverItemToCartItem)
      set({
        items,
        summary: response.summary ?? computeCartSummary(items),
        hasInvalidItems: items.some(
          (i) => i.status === 'out_of_stock' || i.status === 'product_removed',
        ),
      })
    },

    clearError: () => set({ lastError: null }),
  })),
)

// ─── Derived selectors (stable references — use in components) ────────────────

export const selectTotalItems = (s: CartState) => s.summary.unit_count
export const selectIsEmpty = (s: CartState) => s.items.length === 0
export const selectHasIssues = (s: CartState) => s.hasInvalidItems
export const selectSyncing = (s: CartState) => s.syncing